/* =============================================================================
 * M.M.LaFleur — native Color variant picker (partner / Collective products).
 *
 * WHY THIS EXISTS (Sept 15 2026)
 * ------------------------------
 * The PDP was built for MMLF's own catalogue model: ONE PRODUCT PER COLOURWAY,
 * titled "Style :: Colour" and grouped by an `exchange-*` collection. Partner
 * products (Echo scarves and bandanas, via Shopify Collective) use the other
 * model — one product with a native Color option. mm_product.liquid had no
 * path for that:
 *
 *   - the Colourway block renders only when the title carries " :: Colour",
 *     so it was blank;
 *   - the buy block only knows about Size, so with no size it pre-filled the
 *     FIRST variant — Add to Bag always added Dusty Rose and Denim Blue could
 *     not be bought at all;
 *   - the gallery showed every colourway's photographs mixed together.
 *
 * snippets/mm_product_media.liquid emits the picker in an inert <template>
 * (so it never takes a gallery grid cell) plus the variant data as JSON, only
 * for products with a Color option of 2+ values and no " :: " title. This file
 * moves the picker into the buy form and keeps three things pointed at the
 * selected colour:
 *
 *   1. [data-mm-variant-input] — the one field mm-editorial.js adds to cart
 *      from. No second cart path.
 *   2. Size chips, if the product also has sizes — their data-mm-variant is
 *      rewritten per colour, since mm_product.liquid binds each chip to the
 *      first variant with that size regardless of colour.
 *   3. The gallery — frames are detached/re-attached (not hidden), because
 *      mm-pdp-gallery.js measures the first child for carousel steps and
 *      rebuilds whenever the frame count changes.
 *
 * HOW A FRAME IS ASSIGNED TO A COLOUR
 * -----------------------------------
 * Primary: the colour code in the filename. Echo ships EN1597_651_10.jpg for
 * Dusty Rose (SKU F-EN1597-651-1) and EN1597_420_7.jpg for Denim Blue. The
 * code is read off each variant's own featured image, so this is only used
 * when every variant yields a distinct code. A frame whose code matches no
 * variant belongs to a colourway not on sale (EN1597_001_* here) and is held
 * back while filtering. A frame with no code joins the colour of the nearest
 * variant image before it — Shopify's standard grouping.
 *
 * Fallback: grouping alone. If filtering would leave a colour with nothing,
 * the full gallery is shown — a crowded gallery is recoverable, an empty one
 * is not (same rule as the Exclude filter in mm_product.liquid).
 * ========================================================================== */

(function () {
  "use strict";

  if (window.__mmVColorBound) {
    if (typeof window.__mmVColorInit === "function") window.__mmVColorInit();
    return;
  }
  window.__mmVColorBound = true;

  var UUID = /_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  var EXT = /\.(jpe?g|png|webp|avif|gif)$/i;
  var SIZE = /[-_]\d{2,4}x\d{2,4}/gi;
  var ORPHAN = "\u0000orphan";

  function fileKey(src) {
    if (!src) return "";
    var file = src.split("?")[0].split("/").pop() || "";
    return file.replace(EXT, "").replace(UUID, "").replace(SIZE, "").toLowerCase();
  }

  /* "en1597_651_10" -> "651". Needs style_code_index; two parts is no code. */
  function codeOf(key) {
    var parts = key.split("_");
    return parts.length >= 3 ? parts[1] : "";
  }

  function imgSrc(item) {
    var img = item.querySelector("img");
    if (!img) return "";
    var set = img.getAttribute("srcset");
    if (set) return set.split(",")[0].trim().split(/\s+/)[0];
    return img.getAttribute("src") || "";
  }

  function setText(el, text) {
    if (el && el.textContent !== text) el.textContent = text;
  }

  function init() {
    var data = document.querySelector("script[data-mm-vcolor-data]:not([data-mm-vc-done])");
    if (!data) return;

    var cfg;
    try { cfg = JSON.parse(data.textContent); } catch (e) { return; }
    data.setAttribute("data-mm-vc-done", "1");

    var tpl = document.querySelector("template[data-mm-vcolor-tpl]");
    var form = document.querySelector("[data-mm-product-form]");
    if (!tpl || !form || !cfg.variants || !cfg.variants.length) return;

    var ci = cfg.colorIndex;
    var si = typeof cfg.sizeIndex === "number" ? cfg.sizeIndex : -1;

    /* -- picker into the buy form ------------------------------------------ */
    var picker = tpl.content.firstElementChild.cloneNode(true);
    var anchor = form.querySelector(".mm-pdp-block, .mm-pdp-buy");
    if (anchor) form.insertBefore(picker, anchor);
    else form.appendChild(picker);

    var nameEl = picker.querySelector("[data-mm-color-name]");
    var swatches = Array.prototype.slice.call(picker.querySelectorAll("[data-mm-vswatch]"));
    var input = form.querySelector("[data-mm-variant-input]");
    var submit = form.querySelector("[data-mm-submit]");
    var priceEl = document.querySelector(".mm-pdp-price");

    /* -- gallery colour assignment ----------------------------------------- */
    var gallery = document.querySelector(".mm-gallery");
    var frames = gallery
      ? Array.prototype.slice.call(gallery.querySelectorAll(":scope > .mm-gallery-item"))
      : [];
    var hiddenByUs = [];

    var mediaColour = {};
    var codeColour = {};
    var codeCount = 0;
    var colourCount = {};
    cfg.variants.forEach(function (v) {
      var c = v.options[ci];
      if (v.media) mediaColour[String(v.media)] = c;
      if (colourCount[c]) return;
      colourCount[c] = true;
      var code = codeOf(fileKey(v.src));
      if (code && !codeColour[code]) { codeColour[code] = c; codeCount += 1; }
    });
    var colours = Object.keys(colourCount);
    var useCodes = codeCount === colours.length && colours.length > 1;

    var current = null;
    frames.forEach(function (f, i) {
      f.__mmVcIndex = i;
      var id = f.getAttribute("data-mm-media-id") || "";
      var code = useCodes ? codeOf(fileKey(imgSrc(f))) : "";
      var colour;
      if (mediaColour[id] !== undefined) {
        colour = mediaColour[id];
        current = colour;
      } else if (code) {
        colour = codeColour[code] !== undefined ? codeColour[code] : ORPHAN;
        if (colour !== ORPHAN) current = colour;
      } else {
        colour = current; /* null = shared by every colour */
      }
      f.__mmVcColour = colour;
    });

    var canFilter = Object.keys(mediaColour).length > 0 && frames.length > 1;

    function filterGallery(colour) {
      if (!gallery || !canFilter) return;

      var pool = frames.filter(function (f) {
        return f.isConnected || hiddenByUs.indexOf(f) !== -1;
      });
      var want = pool.filter(function (f) {
        return f.__mmVcColour === null || f.__mmVcColour === colour;
      });
      /* Nothing specific to this colour — show everything rather than a
         gallery of shared shots or nothing at all. */
      var specific = want.some(function (f) { return f.__mmVcColour === colour; });
      if (!specific) want = pool;

      hiddenByUs = [];
      pool.forEach(function (f) {
        if (want.indexOf(f) === -1) {
          if (f.parentNode) f.parentNode.removeChild(f);
          hiddenByUs.push(f);
        }
      });
      want.sort(function (a, b) { return a.__mmVcIndex - b.__mmVcIndex; });
      want.forEach(function (f) { gallery.appendChild(f); });

      gallery.scrollLeft = 0;
    }

    /* -- variant resolution ------------------------------------------------ */
    function variantFor(colour, size) {
      var match = null;
      for (var i = 0; i < cfg.variants.length; i += 1) {
        var v = cfg.variants[i];
        if (v.options[ci] !== colour) continue;
        if (si >= 0 && size !== undefined && v.options[si] !== size) continue;
        if (v.available) return v;
        if (!match) match = v;
      }
      return match;
    }

    function syncPrice(v) {
      if (!cfg.priceVaries || !priceEl || !v) return;
      var s = priceEl.querySelector("s");
      if (v.compare) {
        if (!s) { s = document.createElement("s"); priceEl.insertBefore(s, priceEl.firstChild); }
        setText(s, v.compare);
      } else if (s) {
        s.parentNode.removeChild(s);
      }
      /* The price is the first bare text node after the <s>. */
      for (var n = priceEl.firstChild; n; n = n.nextSibling) {
        if (n.nodeType === 3 && n.textContent.trim()) {
          n.textContent = " " + v.price + " ";
          break;
        }
      }
    }

    function setUrl(v) {
      if (!v || !window.history || !window.history.replaceState) return;
      try {
        var url = new URL(window.location.href);
        url.searchParams.set("variant", v.id);
        window.history.replaceState(window.history.state, "", url.toString());
      } catch (e) { /* non-fatal */ }
    }

    function syncBuy(colour) {
      var sizeGroup = form.querySelector("[data-mm-size-group]");

      if (si >= 0 && sizeGroup) {
        var chosen = null;
        Array.prototype.forEach.call(sizeGroup.querySelectorAll("[data-mm-size]"), function (chip) {
          var v = variantFor(colour, chip.textContent.trim());
          if (v) chip.setAttribute("data-mm-variant", v.id);
          else chip.removeAttribute("data-mm-variant");
          var off = !v || !v.available;
          chip.disabled = off;
          if (chip.getAttribute("aria-pressed") === "true") {
            if (off) chip.setAttribute("aria-pressed", "false");
            else chosen = v;
          }
        });
        if (input) input.value = chosen ? chosen.id : "";
        if (submit && !chosen) submit.disabled = true;
        if (chosen) { syncPrice(chosen); setUrl(chosen); }
        return;
      }

      var v = variantFor(colour);
      if (!v) return;
      if (input) input.value = v.id;
      if (submit) {
        submit.disabled = !v.available;
        setText(submit, v.available
          ? (submit.getAttribute("data-mm-ready-label") || "Add to bag")
          : "Sold out");
      }
      syncPrice(v);
      setUrl(v);
    }

    function select(colour, initial) {
      swatches.forEach(function (b) {
        var on = b.getAttribute("data-value") === colour;
        b.setAttribute("aria-checked", String(on));
        b.setAttribute("aria-current", String(on));
      });
      if (nameEl) {
        nameEl.setAttribute("data-mm-default", colour);
        setText(nameEl, colour);
      }
      syncBuy(colour);
      filterGallery(colour);
      if (!initial) {
        document.dispatchEvent(new CustomEvent("mm:variant-color", { detail: { color: colour } }));
      }
    }

    picker.addEventListener("click", function (event) {
      var b = event.target.closest && event.target.closest("[data-mm-vswatch]");
      if (!b || b.getAttribute("data-mm-soldout") === "true") return;
      event.preventDefault();
      select(b.getAttribute("data-value"), false);
    });

    /* Arrow keys move through the radiogroup, as a native radio set would. */
    picker.addEventListener("keydown", function (event) {
      if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;
      var live = swatches.filter(function (b) { return b.getAttribute("data-mm-soldout") !== "true"; });
      var at = live.indexOf(document.activeElement);
      if (at === -1) return;
      event.preventDefault();
      var next = live[(at + (event.key === "ArrowRight" ? 1 : -1) + live.length) % live.length];
      next.focus();
      select(next.getAttribute("data-value"), false);
    });

    picker.removeAttribute("hidden");
    select(cfg.selected, true);
  }

  window.__mmVColorInit = init;

  function start() { window.requestAnimationFrame(init); }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
  document.addEventListener("shopify:section:load", start);

  /* Barba swaps <main>; a fresh, unprocessed data block means a new PDP. */
  var pending = null;
  new MutationObserver(function () {
    if (pending) return;
    pending = window.setTimeout(function () {
      pending = null;
      if (document.querySelector("script[data-mm-vcolor-data]:not([data-mm-vc-done])")) init();
    }, 80);
  }).observe(document.documentElement, { childList: true, subtree: true });
}());
